import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveDialog from '../common/ResponsiveDialog';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import e2iApi from '../../services/e2iApi';
import type { Estimate, EstimateLine } from '../../types/e2i';
import { ESTIMATE_STATUS } from '../../types/e2i';
import { errorText, estimateTotal, formatAmount, formatDateTime, formatQuantity, groupByCostType, lineAmount } from '../../utils/e2i';
import EstimateLineDialog from './EstimateLineDialog';
import { StatusChip } from './StatusChip';
import { WorkspaceTabProps, byRevisionDesc } from './workspace';

interface Draft { remarks: string; lines: EstimateLine[] }
const draftOf = (e: Estimate): Draft => ({ remarks: e.remarks || '', lines: (e.lines || []).map((l) => ({ ...l })) });
type Row = { line: EstimateLine; index: number };

/** The bill of quantities by cost type, in revisions: prepare, post, revise, and turn into a quotation. */
const EstimateTab: React.FC<WorkspaceTabProps> = ({ ws, config, currency, onWorkspace, notify, goTo }) => {
  const perms = ws.permissions;
  const estimates = useMemo(() => byRevisionDesc(ws.estimates || []), [ws.estimates]);
  const latest = estimates[0] || null;
  const [selectedId, setSelectedId] = useState<number | null>(latest?.estimateId ?? null);
  const selected = estimates.find((e) => e.estimateId === selectedId) || latest;
  const [draft, setDraft] = useState<Draft | null>(selected ? draftOf(selected) : null);
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const shownId = useRef<number | null>(selected?.estimateId ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lineDialog, setLineDialog] = useState<{ open: boolean; index: number | null; costType: string }>({ open: false, index: null, costType: '' });
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  // Counts the edits, so a save that answers after more typing does not drop that typing.
  const editVersion = useRef(0);
  const setDirty = (v: boolean) => { if (v) editVersion.current += 1; dirtyRef.current = v; setDirtyState(v); };

  // A new revision (made here or elsewhere) is shown at once.
  const latestId = latest?.estimateId ?? null;
  useLayoutEffect(() => { if (latestId !== null) setSelectedId(latestId); }, [latestId]);

  // The server's estimate, unless the user is editing that same estimate (before paint, so a new revision never
  // shows for a frame without its draft).
  useLayoutEffect(() => {
    if (!selected) { setDraft(null); shownId.current = null; dirtyRef.current = false; setDirtyState(false); return; }
    if (shownId.current === selected.estimateId && dirtyRef.current) return;
    shownId.current = selected.estimateId;
    setDraft(draftOf(selected));
    dirtyRef.current = false;
    setDirtyState(false);
  }, [selected]);

  const editable = !!selected && selected.status === 'Draft' && perms.canEstimate;
  const hasDraft = estimates.some((e) => e.status === 'Draft');
  const lines = useMemo(() => (draft?.lines || []).map((l) => ({ ...l, amount: lineAmount(l.quantity, l.unitCost) })), [draft]);
  const groups = useMemo(() => groupByCostType(lines, config.costTypes), [lines, config.costTypes]);
  const total = estimateTotal(lines);
  const isBusy = busy !== null;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(errorText(e, 'The action failed'));
    } finally {
      setBusy(null);
    }
  };

  const problemsOf = (d: Draft | null): string[] => {
    if (!d?.lines.length) return ['Add at least one line'];
    const bad = d.lines.filter((l) => !l.description.trim() || !(l.quantity > 0) || l.unitCost < 0 || !l.costType).length;
    return bad ? [`${bad} line${bad === 1 ? ' is' : 's are'} incomplete`] : [];
  };

  const saveDraft = async (): Promise<void> => {
    if (!selected || !draft) return;
    const version = editVersion.current;
    const next = await e2iApi.saveEstimate(selected.estimateId, {
      remarks: draft.remarks.trim() || null,
      lines: draft.lines.map((l, i) => ({ ...l, lineNum: i + 1, amount: lineAmount(l.quantity, l.unitCost) })),
    });
    // What was typed while saving stays on screen, still unsaved.
    if (editVersion.current === version) setDirty(false);
    onWorkspace(next);
  };

  const save = () => run('save', async () => {
    await saveDraft();
    notify('Estimate saved', 'success');
  });

  const post = () => run('post', async () => {
    if (!selected) return;
    const problems = problemsOf(draft);
    if (problems.length) throw new Error(`${problems.join('; ')}.`);
    if (dirtyRef.current) await saveDraft();
    const next = await e2iApi.postEstimate(selected.estimateId);
    setDirty(false);
    onWorkspace(next);
    notify(`Estimate R${selected.revision} posted`, 'success');
  });

  const newRevision = () => run('revise', async () => {
    const next = await e2iApi.newEstimate(ws.enquiry.enquiryId);
    setDirty(false);
    onWorkspace(next);
    notify(latest ? 'A new estimate revision was opened from the latest one' : 'The estimate was started', 'success');
  });

  const toQuotation = () => run('quote', async () => {
    if (!selected) return;
    const next = await e2iApi.quotationFromEstimate(selected.estimateId);
    onWorkspace(next);
    notify('The quotation was prepared from the estimate', 'success');
    goTo('quotation');
  });

  const discard = () => {
    if (!selected) return;
    setDraft(draftOf(selected));
    setDirty(false);
  };

  const applyLine = (line: Omit<EstimateLine, 'lineNum'>) => {
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.lines];
      if (lineDialog.index === null) list.push({ ...line, lineNum: list.length + 1 });
      else list[lineDialog.index] = { ...list[lineDialog.index], ...line };
      return { ...d, lines: list };
    });
    setDirty(true);
    setLineDialog((s) => ({ ...s, open: false }));
  };

  const removeLine = (index: number) => {
    setDraft((d) => (d ? { ...d, lines: d.lines.filter((_l, i) => i !== index).map((l, i) => ({ ...l, lineNum: i + 1 })) } : d));
    setDirty(true);
    setDeleteIndex(null);
  };

  const columns: ResponsiveTableColumn<Row>[] = [
    { id: 'n', header: '#', cell: (r) => r.index + 1, width: 40 },
    {
      id: 'desc',
      header: 'Description',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.line.description}</Typography>
          {r.line.itemCode ? <Typography variant="caption" color="text.secondary">Item {r.line.itemCode}</Typography> : null}
        </Box>
      ),
    },
    { id: 'qty', header: 'Quantity', align: 'right', cell: (r) => `${formatQuantity(r.line.quantity)}${r.line.uom ? ` ${r.line.uom}` : ''}` },
    { id: 'cost', header: `Unit cost ${currency}`.trim(), align: 'right', cell: (r) => formatAmount(r.line.unitCost) },
    { id: 'amount', header: `Amount ${currency}`.trim(), align: 'right', cell: (r) => <Typography variant="body2" fontWeight={600}>{formatAmount(r.line.amount)}</Typography> },
    ...(editable ? [{
      id: 'actions',
      header: '',
      align: 'right' as const,
      cell: (r: Row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton size="small" aria-label={`Edit line ${r.index + 1}`} onClick={() => setLineDialog({ open: true, index: r.index, costType: r.line.costType })} disabled={isBusy}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label={`Remove line ${r.index + 1}`} onClick={() => setDeleteIndex(r.index)} disabled={isBusy}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }] : []),
  ];

  if (!selected) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {ws.enquiry.status === 'Posted'
              ? 'No estimate yet. The estimate is the bill of quantities by cost type (labour, material, subcontract...) that the quotation is priced from.'
              : 'Post the enquiry first; the estimate is prepared on a posted enquiry.'}
          </Typography>
          {ws.enquiry.status === 'Posted' && perms.canEstimate ? (
            <Button startIcon={<AddIcon />} onClick={() => { void newRevision(); }} disabled={isBusy}>{busy === 'revise' ? 'Starting…' : 'Start the estimate'}</Button>
          ) : null}
          {error ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert> : null}
        </Stack>
      </Paper>
    );
  }

  const dialogLine = lineDialog.index !== null ? lines[lineDialog.index] || null : null;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            id="e2i-est-revision"
            select
            label="Revision"
            value={selected.estimateId}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            disabled={dirty}
            helperText={dirty ? 'Save or discard the changes to see another revision' : undefined}
            sx={{ minWidth: 220 }}
          >
            {estimates.map((e) => <MenuItem key={e.estimateId} value={e.estimateId}>R{e.revision} · {ESTIMATE_STATUS[e.status]?.label || e.status}</MenuItem>)}
          </TextField>
          <Box>
            <Typography variant="body2" fontWeight={600}>{selected.estimateNo || 'Estimate'} · R{selected.revision}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selected.createdByName ? `Prepared by ${selected.createdByName}` : ''}
              {selected.postedAt ? `${selected.createdByName ? ' · ' : ''}posted ${formatDateTime(selected.postedAt)}` : ''}
            </Typography>
          </Box>
          <StatusChip status={ESTIMATE_STATUS[selected.status]} fallback={selected.status} />
          <Box sx={{ flexGrow: 1 }} />
          {dirty ? <Typography variant="caption" color="text.secondary">Unsaved changes</Typography> : null}
          {editable ? (
            <>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLineDialog({ open: true, index: null, costType: lines[lines.length - 1]?.costType || config.costTypes[0]?.code || '' })} disabled={isBusy}>Add line</Button>
              {dirty ? <Button size="small" variant="text" onClick={discard} disabled={isBusy}>Discard changes</Button> : null}
              <Button size="small" variant="outlined" onClick={() => { void save(); }} disabled={isBusy || !dirty}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
              <Button size="small" onClick={() => { void post(); }} disabled={isBusy}>{busy === 'post' ? 'Posting…' : 'Post estimate'}</Button>
            </>
          ) : null}
          {perms.canEstimate && !hasDraft && latest?.status === 'Posted' && selected.estimateId === latest.estimateId ? (
            <Button size="small" variant="outlined" onClick={() => { void newRevision(); }} disabled={isBusy}>{busy === 'revise' ? 'Opening…' : 'Revise'}</Button>
          ) : null}
          {perms.canQuote && selected.status === 'Posted' ? (
            <Button size="small" onClick={() => { void toQuotation(); }} disabled={isBusy}>{busy === 'quote' ? 'Preparing…' : 'Prepare quotation'}</Button>
          ) : null}
        </Stack>
        {error ? <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </Paper>

      {groups.length ? groups.map((g) => (
        <Paper key={g.code || 'none'} sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" component="h3" sx={{ mr: 'auto' }}>{g.name}{g.code ? ` (${g.code})` : ''}</Typography>
            <Typography variant="body2">Subtotal <strong>{formatAmount(g.subtotal)}</strong> {currency}</Typography>
          </Stack>
          <ResponsiveTable ariaLabel={`Estimate lines: ${g.name}`} columns={columns} rows={g.lines} getRowKey={(r) => r.index} />
        </Paper>
      )) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">{editable ? 'No lines yet: add the labour, material, subcontract and other costs.' : 'No lines.'}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          {editable ? (
            <TextField
              id="e2i-est-remarks"
              label="Remarks"
              value={draft?.remarks || ''}
              onChange={(e) => { const v = e.target.value.slice(0, 4000); setDraft((d) => (d ? { ...d, remarks: v } : d)); setDirty(true); }}
              multiline
              minRows={2}
              fullWidth
            />
          ) : (
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Remarks</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selected.remarks || '—'}</Typography>
            </Box>
          )}
          <Box sx={{ minWidth: 220, textAlign: { sm: 'right' } }}>
            <Typography variant="caption" color="text.secondary">Total cost</Typography>
            <Typography variant="h6" component="p">{formatAmount(dirty || editable ? total : selected.totalCost)} {currency}</Typography>
          </Box>
        </Stack>
      </Paper>

      <EstimateLineDialog
        open={lineDialog.open}
        line={dialogLine}
        costTypes={config.costTypes}
        defaultCostType={lineDialog.costType}
        currency={currency}
        onClose={() => setLineDialog((s) => ({ ...s, open: false }))}
        onSave={applyLine}
      />

      <ResponsiveDialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="e2i-est-del-title">
        <DialogTitle id="e2i-est-del-title">Remove line {deleteIndex !== null ? deleteIndex + 1 : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{deleteIndex !== null ? lines[deleteIndex]?.description : ''}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteIndex(null)}>Keep</Button>
          <Button color="error" onClick={() => { if (deleteIndex !== null) removeLine(deleteIndex); }}>Remove</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Stack>
  );
};

export default EstimateTab;
